import React from "react";
import { NavLink, type NavLinkProps } from "react-router-dom";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { noop } from "lodash";

import { showFeatureModal } from "~/billing";
import { UpgradeIcon } from "~/billing/BillingIdentity";

import type { BillingFeature } from "~/__generated__/utilsDummyQuery.graphql";

import panelSectionStyles from "./PanelSection.module.css";

type PrimaryNavLinkProps = Omit<NavLinkProps, "className" | "activeClassName">;

/** One of the larger links at the top of the panel in a different font */
export default function PrimaryNavLink(props: PrimaryNavLinkProps) {
	return <NavLink className={panelSectionStyles.menuLink} activeClassName={panelSectionStyles.active} {...props} />;
}

/** A green dot that matches the width of a PrimaryNavLink's icon */
export function PrimaryNavLinkUnreadDot() {
	return (
		<div className={panelSectionStyles.unreadDotContainer}>
			<span className={panelSectionStyles.unreadDot} />
		</div>
	);
}

type PaywalledPrimaryNavLinkProps = PrimaryNavLinkProps & {
	isPaywalled: boolean;
	feature: BillingFeature;
	icon: IconDefinition;
	children: React.ReactNode;
};

/** A PrimaryNavLink that shows an UpgradeIcon and shows a paywall if the feature is paywalled */
export function PaywalledPrimaryNavLink({ to, icon, isPaywalled, feature, children }: PaywalledPrimaryNavLinkProps) {
	const showPaywall = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			if (isPaywalled) {
				event.preventDefault();
				showFeatureModal(feature);
			}
		},
		[feature, isPaywalled],
	);

	return (
		<PrimaryNavLink to={to} onClick={isPaywalled ? showPaywall : noop}>
			{isPaywalled ? (
				// Need to set the color here to override color from .panelSectionContent class
				<UpgradeIcon style={{ color: "var(--billingColor)" }} fixedWidth />
			) : (
				<FontAwesomeIcon icon={icon} fixedWidth />
			)}
			{children}
		</PrimaryNavLink>
	);
}
